import {
  TellescopeModelFromName,
  useAuth,
  useCachedTags,
  useRolesQuery,
  useTellescopeMutation,
  useUsersQuery,
} from "@tellescope/tanstack-query";
import { ADMIN_ROLE } from "@tellescope/constants";
import { useDateFnsFormattersWithLocale, type VirtualizedColumnDef, VirtualizedDataTableProps } from "@tellescope/ui";
import { Tooltip } from "@tellescope/design-system";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useTableView, TELLESCOPE_2_TABLE_VIEW_PREFIX } from "./useTableView";
import { useTableViewConfig, type UseTableViewConfigResult } from "./useTableViewConfig";
import { DEFAULT_TEAM_VIEW, useTeamDefaultViewShallowStore } from "../store";
import { type TeamMembersFilters, countTeamMembersActiveFilters } from "../components/team/teamMembersFilters";
import { TeamMemberBadgeList } from "../components/team/TeamMemberBadgeList";
import { useTranslation } from "react-i18next";

type TeamMember = TellescopeModelFromName<"users">;
type TeamMembersQueryResult = ReturnType<typeof useUsersQuery>;

export interface UseTeamMembersTableArgs {
  viewId?: string;
  setViewId?: Dispatch<SetStateAction<string>>;
  onSelectUser?: (userId: string) => void;
}

export interface UseTeamMembersTableResult extends UseTableViewConfigResult<TeamMembersFilters> {
  usersQuery: TeamMembersQueryResult;
  virtualizedTableProps: Partial<VirtualizedDataTableProps<TeamMember>> &
    Pick<VirtualizedDataTableProps<TeamMember>, "data" | "columns">;
  views: TellescopeModelFromName<"table_views">[];
  selectedView: TellescopeModelFromName<"table_views">;
  viewId: string;
  setViewId: Dispatch<SetStateAction<string>>;
  isLoadingViews: boolean;
  createTableViewMutation: ReturnType<typeof useTellescopeMutation<"table_views", "createOne">>;
  deleteTableViewMutation: ReturnType<typeof useTellescopeMutation<"table_views", "deleteOne">>;
  tagOptions: string[];
  roleOptions: string[];
  TELLESCOPE_2_TABLE_VIEW_PREFIX: typeof TELLESCOPE_2_TABLE_VIEW_PREFIX;
}

export const useTeamMembersTable = ({
  viewId: providedViewId,
  setViewId,
  onSelectUser,
}: UseTeamMembersTableArgs = {}): UseTeamMembersTableResult => {
  const { t } = useTranslation("settings");
  const { formatDistanceToNowStrict } = useDateFnsFormattersWithLocale();
  const { currentUser } = useAuth();
  const userId = currentUser.id;

  const [searchQuery, setSearchQuery] = useState("");

  const { defaultViewByUserId, setUserDefaultView } = useTeamDefaultViewShallowStore(
    "defaultViewByUserId",
    "setUserDefaultView"
  );

  const fieldToColumnConfigMap = useMemo<Record<string, Partial<VirtualizedColumnDef<TeamMember>>>>(
    () => ({
      ID: {
        accessorKey: "id",
        label: t("ID"),
        width: "10%",
        cell: ({ row }) => (
          <Tooltip>
            <Tooltip.Trigger className="w-full">
              <div className="text-xs font-mono truncate text-muted-foreground" title={row.original.id}>
                {row.original.id}
              </div>
            </Tooltip.Trigger>
            <Tooltip.Content>{row.original.id}</Tooltip.Content>
          </Tooltip>
        ),
      },
      Email: {
        accessorKey: "email",
        label: t("Email"),
        minWidth: 150,
        cell: ({ row }) => (
          <div className="text-sm truncate min-w-[150px]" title={row.original.email || undefined}>
            {row.original.email || <span className="text-muted-foreground">{t("No email")}</span>}
          </div>
        ),
      },
      FirstName: {
        accessorKey: "fname",
        label: t("First Name"),
        width: "120px",
        cell: ({ row }) => <div className="text-sm truncate">{row.original.fname || "—"}</div>,
      },
      LastName: {
        accessorKey: "lname",
        label: t("Last Name"),
        width: "120px",
        cell: ({ row }) => <div className="text-sm truncate">{row.original.lname || "—"}</div>,
      },
      LastActive: {
        accessorKey: "lastActive",
        label: t("Last Active"),
        width: "120px",
        cell: ({ row }) => (
          <div className="text-sm truncate">
            {row.original.lastActive ? formatDistanceToNowStrict(row.original.lastActive, { addSuffix: true }) : "—"}
          </div>
        ),
      },
      Tags: {
        accessorKey: "tags",
        label: t("Tags"),
        cell: ({ row }) => <TeamMemberBadgeList values={row.original.tags ?? []} emptyLabel={t("No tags")} />,
      },
      Role: {
        accessorKey: "roles",
        label: t("Role"),
        cell: ({ row }) => <TeamMemberBadgeList values={row.original.roles ?? []} emptyLabel={t("Non-Admin")} />,
      },
    }),
    [formatDistanceToNowStrict, t]
  );

  const {
    views,
    selectedView,
    columns,
    viewId,
    setViewId: setViewIdInternal,
    isLoadingViews,
    isDefaultView,
  } = useTableView<TeamMember>({
    page: "T2-TeamMembers",
    defaultView: defaultViewByUserId[userId] ?? DEFAULT_TEAM_VIEW,
    fieldToColumnConfigMap,
    viewId: providedViewId,
    setViewId,
    sortable: true,
  });

  const activeFilters = useMemo<TeamMembersFilters>(() => {
    const { sorting: _sorting, ...filters } = selectedView.filter ?? {};
    return filters;
  }, [selectedView.filter]);

  const viewConfig = useTableViewConfig<TeamMembersFilters>({
    isDefaultView,
    userId,
    selectedView,
    columns,
    allColumnConfigMap: fieldToColumnConfigMap,
    defaultView: DEFAULT_TEAM_VIEW,
    setUserDefaultView,
    activeFilters,
    countActiveFilters: countTeamMembersActiveFilters,
  });

  const usersQuery = useUsersQuery();
  const users = useMemo(() => usersQuery.data ?? [], [usersQuery.data]);

  const { data: roleRecords } = useRolesQuery();

  const { data: tagOptions } = useCachedTags({ model: "users" });

  // All defined roles (plus the implicit Admin and Non-Admin roles) — powers the Roles filter options.
  const roleOptions = useMemo(() => {
    const roles = new Set<string>([ADMIN_ROLE, ""]);
    for (const { role } of roleRecords ?? []) roles.add(role);
    return Array.from(roles).sort();
  }, [roleRecords]);

  // Tags/roles/lastActive filters are applied client-side over the loaded users.
  const filteredUsers = useMemo(() => {
    const { tags, roles, lastActiveRange } = viewConfig.activeFilters;
    if (!tags?.length && !roles?.length && !lastActiveRange) return users;
    return users.filter((user) => {
      if (tags?.length && !(user.tags ?? []).some((tag) => tags.includes(tag))) return false;
      if (roles?.length && !(user.roles ?? []).some((role) => roles.includes(role))) return false;
      if (lastActiveRange) {
        const lastActive = user.lastActive?.getTime();
        if (lastActive === undefined) return false;
        if (lastActiveRange.from !== undefined && lastActive < lastActiveRange.from) return false;
        if (lastActiveRange.to !== undefined && lastActive > lastActiveRange.to) return false;
      }
      return true;
    });
  }, [users, viewConfig.activeFilters]);

  const createTableViewMutation = useTellescopeMutation({ model: "table_views", method: "createOne" });
  const deleteTableViewMutation = useTellescopeMutation({ model: "table_views", method: "deleteOne" });

  const virtualizedTableProps = {
    data: filteredUsers,
    columns,
    getRowId: (row: TeamMember) => row.id,
    enableInternalFiltering: true,
    globalFilter: searchQuery,
    onGlobalFilterChange: setSearchQuery,
    sorting: viewConfig.sorting,
    setSorting: viewConfig.onSortingChange,
    onLoadMore: () => {
      usersQuery.fetchNextPage();
    },
    hasMoreData: usersQuery.hasNextPage,
    isEmpty: usersQuery.isFetched && filteredUsers.length === 0,
    isLoadingMore: usersQuery.isLoading || usersQuery.isFetchingNextPage,
    headerClassName: "bg-gray-50",
    onRowClick: onSelectUser ? (user) => onSelectUser(user.id) : undefined,
  } satisfies Partial<VirtualizedDataTableProps<TeamMember>>;

  return {
    usersQuery,
    virtualizedTableProps,
    views,
    selectedView,
    viewId,
    setViewId: setViewIdInternal,
    isLoadingViews,
    createTableViewMutation,
    deleteTableViewMutation,
    tagOptions,
    roleOptions,
    TELLESCOPE_2_TABLE_VIEW_PREFIX,
    ...viewConfig,
  };
};
